import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api, fmtDateTime } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const LEVEL = { critical: "border-l-red-500", warning: "border-l-amber-500", info: "border-l-cyan-500" };

export const Notifications = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const seen = useRef(null);
  const nav = useNavigate();

  const load = useCallback(async () => {
    const r = await api.get("/notifications").catch(() => null);
    if (!r) return;
    const fresh = r.data.items.filter((n) => !n.read && n.level === "critical");
    if (seen.current && "Notification" in window && Notification.permission === "granted") {
      fresh.filter((n) => !seen.current.has(n.id)).forEach((n) => new Notification(n.title, { body: n.message }));
    }
    seen.current = new Set(r.data.items.map((n) => n.id));
    setData(r.data);
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const open = async (o) => {
    if (o && "Notification" in window && Notification.permission === "default") Notification.requestPermission();
    if (!o && data.unread) {
      await api.post("/notifications/read");
      load();
    }
  };

  return (
    <Popover onOpenChange={open}>
      <PopoverTrigger asChild>
        <button className="relative flex h-11 w-11 items-center justify-center rounded-md hover:bg-slate-800" data-testid="notifications-bell">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span className="absolute right-1 top-1 min-w-[18px] rounded-full bg-rose-600 px-1 text-center font-mono text-[10px] leading-[18px]" data-testid="notifications-unread-count">{data.unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] border-slate-700 bg-ink-2 p-0" data-testid="notifications-panel">
        <div className="border-b border-slate-800 px-4 py-3 eyebrow">Alerts & escalations</div>
        <div className="max-h-[420px] overflow-y-auto">
          {data.items.map((n) => (
            <button key={n.id} onClick={() => n.link && nav(n.link)} className={`block w-full border-b border-l-4 border-slate-800 px-4 py-3 text-left hover:bg-slate-800/50 ${LEVEL[n.level]} ${n.read ? "opacity-60" : ""}`} data-testid={`notification-${n.id}`}>
              <p className="text-sm font-semibold text-slate-100">{n.title}</p>
              <p className="text-xs text-slate-400">{n.message}</p>
              <p className="mt-1 font-mono text-[10px] text-slate-500">{fmtDateTime(n.created_at)}</p>
            </button>
          ))}
          {!data.items.length && <p className="px-4 py-8 text-center text-sm text-slate-500">No alerts.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
};
